import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, Box, alpha,
  Tooltip, IconButton, Select, MenuItem, Checkbox,
} from '@mui/material';
import ArrowUpwardIcon   from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon    from '@mui/icons-material/UnfoldMore';
import ChevronLeftIcon   from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon  from '@mui/icons-material/ChevronRight';
import VisibilityIcon    from '@mui/icons-material/Visibility';
import EditIcon          from '@mui/icons-material/Edit';
import DeleteIcon        from '@mui/icons-material/Delete';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { tableCheckboxSx, ACTION_VIEW, ACTION_EDIT, ACTION_DELETE, tableNavButtonSx } from './SaaSTokens';

// ─── DESIGN TOKENS — mirrors FilterBar exactly ───────────────────
const ACCENT         = '#38bdf8';   // sky  (primary)
const ACCENT_2       = '#3b82f6';   // blue (secondary)
const BG             = '#0f172a';   // page dark
const SURFACE        = '#111827';   // card
const SURFACE_2      = '#1e293b';   // elevated
const SURFACE_3      = '#0d1525';   // deepest
const TEXT_PRIMARY   = '#f8fafc';
const TEXT_SECONDARY = '#cbd5e1';
const RADIUS_MD = '12px';
const RADIUS_LG = '16px';

// ─── SPRING PRESETS ──────────────────────────────────────────────
const SPRING_SNAPPY = { type: 'spring', stiffness: 420, damping: 26 };
const SPRING_SOFT   = { type: 'spring', stiffness: 300, damping: 28 };
const EASE_OUT      = { duration: 0.22, ease: [0.33, 1, 0.68, 1] };

// ─── MONO LABEL ──────────────────────────────────────────────────
const MonoLabel = ({ children, color = alpha(TEXT_SECONDARY, 0.5), size = '0.6rem', sx = {} }) => (
  <Typography sx={{
    fontFamily: "'DM Mono', 'Fira Mono', monospace",
    fontSize: size,
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color,
    lineHeight: 1,
    ...sx,
  }}>
    {children}
  </Typography>
);

// ─── SORT ICON ───────────────────────────────────────────────────
const SortIcon = ({ active, direction }) => {
  const iconSx = {
    fontSize: 11,
    position: 'absolute',
    color: ACCENT,
    filter: `drop-shadow(0 0 4px ${alpha(ACCENT, 0.7)})`,
  };
  const idleSx = { fontSize: 11, position: 'absolute', color: alpha(TEXT_SECONDARY, 0.25) };

  const v = {
    enter:  { opacity: 0, scale: 0.3 },
    center: { opacity: 1, scale: 1, transition: SPRING_SNAPPY },
    exit:   { opacity: 0, scale: 0.3, transition: { duration: 0.1 } },
  };

  return (
    <Box sx={{ width: 12, height: 12, position: 'relative', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <AnimatePresence mode="wait" initial={false}>
        {active && direction === 'asc'  && <motion.div key="asc"  variants={v} initial="enter" animate="center" exit="exit" style={{ position: 'absolute' }}><ArrowUpwardIcon   sx={iconSx} /></motion.div>}
        {active && direction === 'desc' && <motion.div key="desc" variants={v} initial="enter" animate="center" exit="exit" style={{ position: 'absolute' }}><ArrowDownwardIcon sx={iconSx} /></motion.div>}
        {!active                        && <motion.div key="idle" variants={v} initial="enter" animate="center" exit="exit" style={{ position: 'absolute' }}><UnfoldMoreIcon    sx={idleSx} /></motion.div>}
      </AnimatePresence>
    </Box>
  );
};

// ─── SKELETON ROW ────────────────────────────────────────────────
const SkeletonRow = ({ cols, index }) => (
  <TableRow sx={{ '& .MuiTableCell-root': { border: 'none', py: 0, height: 52 } }}>
    {Array.from({ length: cols }).map((_, ci) => (
      <TableCell key={ci} sx={{ px: 3 }}>
        <Box sx={{
          height: 7,
          borderRadius: '6px',
          width: ci === 0 ? '52%' : `${28 + Math.sin(index * 2 + ci * 1.5) * 22}%`,
          background: `linear-gradient(90deg,
            ${alpha(SURFACE_2, 0.8)} 0%,
            ${alpha(ACCENT, 0.1)}   40%,
            ${alpha(SURFACE_2, 0.8)} 100%)`,
          backgroundSize: '300% 100%',
          animation: `skel-shimmer ${1.8 + index * 0.07}s ease-in-out ${index * 0.055}s infinite`,
          '@keyframes skel-shimmer': {
            '0%':   { backgroundPosition: '100% 0' },
            '100%': { backgroundPosition: '-100% 0' },
          },
        }} />
      </TableCell>
    ))}
  </TableRow>
);

// ─── LOADING STATE ───────────────────────────────────────────────
const LOAD_MSGS = ['Fetching records…', 'Loading data…', 'Almost ready…', 'Preparing rows…'];

const LoadingState = ({ colSpan }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % LOAD_MSGS.length), 1800);
    return () => clearInterval(t);
  }, []);

  return (
    <TableRow sx={{ '&:hover': { background: 'transparent !important' } }}>
      <TableCell colSpan={colSpan} sx={{ border: 'none', p: 0, position: 'relative' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableBody>{Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} cols={colSpan} index={i} />)}</TableBody>
        </Table>
        {/* Floating label */}
        <Box sx={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `linear-gradient(180deg, ${alpha(BG, 0.1)} 0%, ${alpha(BG, 0.65)} 100%)`,
          pointerEvents: 'none',
        }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={EASE_OUT}
            >
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 2, py: 0.9,
                borderRadius: RADIUS_MD,
                background: alpha(SURFACE_2, 0.9),
                border: `1px solid ${alpha(ACCENT, 0.2)}`,
                backdropFilter: 'blur(8px)',
              }}>
                {/* Spinning ring */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke={ACCENT} strokeWidth="1.8" strokeLinecap="round">
                    <path d="M6 1 A5 5 0 0 1 11 6" />
                  </svg>
                </motion.div>
                <MonoLabel color={alpha(ACCENT, 0.8)} size="0.62rem">{LOAD_MSGS[idx]}</MonoLabel>
              </Box>
            </motion.div>
          </AnimatePresence>
        </Box>
      </TableCell>
    </TableRow>
  );
};

// ─── EMPTY STATE ─────────────────────────────────────────────────
const EmptyState = ({ colSpan, icon, title, description, action }) => (
  <TableRow sx={{ '&:hover': { background: 'transparent !important' } }}>
    <TableCell colSpan={colSpan} sx={{ border: 'none', p: 0 }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={SPRING_SOFT}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 24px', gap: 12 }}
      >
        {icon && (
          <motion.div
            initial={{ scale: 0.4, opacity: 0, rotate: -15 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ delay: 0.07, ...SPRING_SNAPPY }}
          >
            <Box sx={{
              width: 64, height: 64, borderRadius: '18px',
              background: `linear-gradient(145deg, ${alpha(SURFACE_2, 0.9)}, ${alpha(SURFACE_3, 0.95)})`,
              border: `1px solid ${alpha(ACCENT, 0.2)}`,
              boxShadow: `0 8px 32px ${alpha('#000', 0.35)}, 0 0 0 6px ${alpha(ACCENT, 0.05)}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {React.cloneElement(icon, { sx: { fontSize: 26, color: alpha(ACCENT, 0.65) } })}
            </Box>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, ...EASE_OUT }}>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '0.95rem', color: alpha(TEXT_PRIMARY, 0.8) }}>
            {title}
          </Typography>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.22 }}>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.76rem', color: alpha(TEXT_SECONDARY, 0.45), textAlign: 'center', maxWidth: 240, lineHeight: 1.7 }}>
            {description}
          </Typography>
        </motion.div>

        {action && (
          <motion.button
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            onClick={action.onClick}
            style={{
              all: 'unset',
              marginTop: 4,
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 16px',
              borderRadius: RADIUS_MD,
              border: `1px solid ${alpha(ACCENT, 0.3)}`,
              background: alpha(ACCENT, 0.08),
              color: ACCENT,
              fontSize: '0.76rem',
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {action.label}
          </motion.button>
        )}
      </motion.div>
    </TableCell>
  </TableRow>
);

// ─── PAGINATION ───────────────────────────────────────────────────
export const TableListPagination = ({
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 25, 50],
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const canPrev = page > 0;
  const canNext = page < totalPages - 1;

  const from = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const to   = Math.min((page + 1) * rowsPerPage, totalCount);

  const NavBtn = ({ disabled, onClick, children, title }) => (
    <Tooltip title={title} arrow>
      <span>
        <motion.div whileHover={!disabled ? { scale: 1.08 } : {}} whileTap={!disabled ? { scale: 0.9 } : {}}>
          <IconButton disabled={disabled} onClick={onClick} sx={tableNavButtonSx(disabled)}>
            {children}
          </IconButton>
        </motion.div>
      </span>
    </Tooltip>
  );

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: { xs: 'center', sm: 'space-between' },
      flexDirection: { xs: 'column', sm: 'row' },
      px: { xs: 1.5, sm: 2.5 },
      py: 1.2,
      borderTop: `1px solid ${alpha(TEXT_PRIMARY, 0.06)}`,
      background: `linear-gradient(180deg, ${alpha(SURFACE_2, 0.6)} 0%, ${alpha(SURFACE_3, 0.5)} 100%)`,
      gap: 1.5,
    }}>

      {/* Rows per page */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MonoLabel>Rows</MonoLabel>
        <Select
          value={rowsPerPage}
          onChange={e => onRowsPerPageChange({ target: { value: e.target.value } })}
          sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: '0.62rem',
            fontWeight: 600,
            color: alpha(TEXT_SECONDARY, 0.75),
            background: alpha(BG, 0.5),
            borderRadius: '8px',
            '& .MuiOutlinedInput-notchedOutline': { border: `1px solid ${alpha(ACCENT, 0.18)}` },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(ACCENT, 0.38) },
            '& .MuiSvgIcon-root': { color: alpha(ACCENT, 0.5), fontSize: 16 },
            '& .MuiSelect-select': { py: 0.5, px: 1 },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                bgcolor: SURFACE_2,
                border: `1px solid ${alpha(ACCENT, 0.2)}`,
                borderRadius: RADIUS_MD,
                boxShadow: `0 16px 40px ${alpha('#000', 0.4)}`,
                '& .MuiMenuItem-root': {
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.72rem',
                  color: TEXT_PRIMARY,
                  borderRadius: '8px',
                  mx: 0.5,
                  '&:hover':        { bgcolor: alpha(ACCENT, 0.1) },
                  '&.Mui-selected': { bgcolor: alpha(ACCENT, 0.18), color: ACCENT },
                },
              },
            },
          }}
        >
          {rowsPerPageOptions.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
        </Select>
      </Box>

      {/* Record range */}
      <Box sx={{
        px: 1.4, py: 0.55,
        borderRadius: '8px',
        background: alpha(ACCENT, 0.06),
        border: `1px solid ${alpha(ACCENT, 0.18)}`,
      }}>
        <MonoLabel color={alpha(ACCENT, 0.7)} size="0.58rem">
          {from}–{to} of {totalCount}
        </MonoLabel>
      </Box>

      {/* Prev / Next */}
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        <NavBtn disabled={!canPrev} onClick={() => onPageChange({}, page - 1)} title="Previous page">
          <ChevronLeftIcon />
        </NavBtn>
        <NavBtn disabled={!canNext} onClick={() => onPageChange({}, page + 1)} title="Next page">
          <ChevronRightIcon />
        </NavBtn>
      </Box>
    </Box>
  );
};

// ─── ROW ANIMATION VARIANTS ──────────────────────────────────────
const rowVariants = {
  hidden:  { opacity: 0, y: 6 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.025, ...SPRING_SOFT },
  }),
};

// ─── CELL TONE HELPERS ───────────────────────────────────────────
const getColumnTone = (col, index) => {
  if (col.tone) return col.tone;
  if (col.primary || index === 0) return 'primary';
  const key = String(col.key || col.sortKey || '').toLowerCase();
  if (key.includes('action')) return 'action';
  if (key.includes('status') || key.includes('role') || col.align === 'center') return 'badge';
  return 'secondary';
};

const actionBtnSx = (color) => ({
  width: 28,
  height: 28,
  borderRadius: '8px',
  color,
  transition: 'all 0.16s ease',
  '&:hover': {
    color: `${color} !important`,
    backgroundColor: `${alpha(color, 0.14)} !important`,
  },
});

/** Standard view (blue) / edit (orange) / delete (red) action buttons for list tables */
export const TableRowActions = ({
  onView,
  onEdit,
  onDelete,
  showView = true,
  showEdit = true,
  showDelete = true,
  viewTitle = 'View',
  editTitle = 'Edit',
  deleteTitle = 'Delete',
}) => (
  <Box
    sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}
    onClick={(e) => e.stopPropagation()}
  >
    {showView && onView && (
      <Tooltip title={viewTitle} arrow>
        <IconButton size="small" data-action="view" onClick={onView} sx={actionBtnSx(ACTION_VIEW)}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
    {showEdit && onEdit && (
      <Tooltip title={editTitle} arrow>
        <IconButton size="small" data-action="edit" onClick={onEdit} sx={actionBtnSx(ACTION_EDIT)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
    {showDelete && onDelete && (
      <Tooltip title={deleteTitle} arrow>
        <IconButton size="small" data-action="delete" onClick={onDelete} sx={actionBtnSx(ACTION_DELETE)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

const SHARED_CELL_SX = {
  height: { xs: 50, sm: 54 },
  py: 0, px: { xs: 1.5, sm: 2, md: 3 },
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '0.84rem',
  fontWeight: 400,
  color: alpha(TEXT_PRIMARY, 0.82),
  borderBottom: `1px solid ${alpha(TEXT_PRIMARY, 0.05)}`,
  verticalAlign: 'middle',
  transition: 'background-color 0.14s ease',
};

// ═══════════════════════════════════════════════════════════════════
// TABLE LIST
// ═══════════════════════════════════════════════════════════════════
const TableList = ({
  columns             = [],
  data                = [],
  page                = 0,
  rowsPerPage         = 10,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions  = [5, 10, 25, 50],
  loading             = false,
  emptyState          = { icon: null, title: 'No records found', description: 'Nothing to display here yet.' },
  onRowClick,
  streamLabel         = 'DATA STREAM',
  selectable          = false,
  getRowId            = (row) => row?.id,
  selectedIds         = [],
  onSelectionChange,
  sx                  = {},
}) => {
  const prefersReduced = useReducedMotion();

  const [sortField,  setSortField]  = useState(null);
  const [sortDir,    setSortDir]    = useState('asc');
  const [animKey,    setAnimKey]    = useState(0);
  const [hoveredRow, setHoveredRow] = useState(null);

  // ── Sort ──
  const handleSort = useCallback((col) => {
    const field = col.sortKey ?? col.key;
    if (col.sortable === false || field == null) return;
    setAnimKey(k => k + 1);
    setSortDir(d => sortField === field ? (d === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  }, [sortField]);

  const getByPath = (obj, k) =>
    k.includes('.') ? k.split('.').reduce((o, p) => o?.[p], obj) : obj?.[k];

  const sortedData = useMemo(() => {
    if (!sortField || !columns.length) return data;
    const col = columns.find(c => (c.sortKey ?? c.key) === sortField);
    if (!col || col.sortable === false) return data;
    const read = row => typeof col.sortAccessor === 'function' ? col.sortAccessor(row) : getByPath(row, sortField);
    return [...data].sort((a, b) => {
      const av = read(a), bv = read(b);
      if (av == null && bv == null) return 0;
      if (av == null) return sortDir === 'asc' ?  1 : -1;
      if (bv == null) return sortDir === 'asc' ? -1 :  1;
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      const c = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? c : -c;
    });
  }, [data, sortField, sortDir, columns]);

  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const colCount      = columns.length + (selectable ? 1 : 0);
  const selectedSet   = useMemo(() => new Set((selectedIds || []).map(String)), [selectedIds]);

  const pageIds        = useMemo(() => paginatedData.map(row => String(getRowId(row))), [paginatedData, getRowId]);
  const allPageChecked = pageIds.length > 0 && pageIds.every(id => selectedSet.has(id));
  const someChecked    = pageIds.some(id => selectedSet.has(id));

  const toggleAll = (e) => {
    e?.stopPropagation?.();
    if (!onSelectionChange) return;
    if (allPageChecked) {
      onSelectionChange((selectedIds || []).filter(id => !pageIds.includes(String(id))));
    } else {
      onSelectionChange([...new Set([...(selectedIds || []).map(String), ...pageIds])]);
    }
  };

  const toggleRow = (row, e) => {
    e?.stopPropagation?.();
    if (!onSelectionChange) return;
    const id = String(getRowId(row));
    selectedSet.has(id)
      ? onSelectionChange((selectedIds || []).filter(x => String(x) !== id))
      : onSelectionChange([...(selectedIds || []).map(String), id]);
  };

  const renderCell = (col, row, idx) => {
    if (col.render) return col.render(row, idx);
    if (col.key?.includes('.')) return col.key.split('.').reduce((o, k) => o?.[k], row) ?? '—';
    return row[col.key] ?? '—';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={SPRING_SOFT}
      style={{
        borderRadius: RADIUS_LG,
        overflow: 'hidden',
        position: 'relative',
        background: `linear-gradient(160deg, ${alpha(SURFACE, 0.97)}, ${alpha(BG, 0.98)} 50%, ${alpha(SURFACE_3, 0.99)})`,
        border: `1px solid ${alpha(ACCENT_2, 0.2)}`,
        boxShadow: `0 20px 60px ${alpha('#000', 0.48)}, 0 0 0 1px ${alpha(ACCENT, 0.06)}, inset 0 1px 0 ${alpha('#fff', 0.04)}`,
        ...sx,
      }}
    >
      {/* ── Header bar ── */}
      <Box sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        px: { xs: 2, sm: 2.5 }, py: 1.1,
        borderBottom: `1px solid ${alpha(TEXT_PRIMARY, 0.06)}`,
        background: `linear-gradient(90deg, ${alpha(ACCENT, 0.08)} 0%, ${alpha(ACCENT_2, 0.04)} 60%, transparent 100%)`,
        position: 'relative', zIndex: 2,
      }}>
        {/* Left: status dot + label */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
          <Box sx={{ position: 'relative', width: 10, height: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{
              width: 6, height: 6, borderRadius: '50%',
              background: ACCENT,
              boxShadow: `0 0 7px ${alpha(ACCENT, 0.9)}`,
              animation: 'dot-pulse 2s ease-in-out infinite',
              '@keyframes dot-pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.45 } },
            }} />
            <Box sx={{
              position: 'absolute', inset: -2, borderRadius: '50%',
              border: `1px solid ${alpha(ACCENT, 0.4)}`,
              animation: 'dot-ring 2s ease-in-out infinite',
              '@keyframes dot-ring': { '0%,100%': { opacity: 0.6, transform: 'scale(1)' }, '50%': { opacity: 0.15, transform: 'scale(1.35)' } },
            }} />
          </Box>
          <MonoLabel color={alpha(TEXT_PRIMARY, 0.65)} size="0.6rem" sx={{ letterSpacing: '0.12em' }}>
            {streamLabel}
          </MonoLabel>
        </Box>

        {/* Right: record count + selection badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', minWidth: 0 }}>
          <AnimatePresence mode="wait">
            {!loading && (
              <motion.div
                key={sortedData.length}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={SPRING_SNAPPY}
              >
                <Box sx={{
                  px: 1.2, py: 0.4, borderRadius: '7px',
                  background: alpha(ACCENT, 0.07),
                  border: `1px solid ${alpha(ACCENT, 0.18)}`,
                }}>
                  <MonoLabel color={alpha(ACCENT, 0.7)} size="0.57rem">
                    {sortedData.length} records
                  </MonoLabel>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {selectable && selectedSet.size > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.7, x: 6 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.7, x: 6 }}
                transition={SPRING_SNAPPY}
              >
                <Box sx={{
                  px: 1.2, py: 0.4, borderRadius: '7px',
                  background: alpha(ACCENT_2, 0.14),
                  border: `1px solid ${alpha(ACCENT_2, 0.35)}`,
                }}>
                  <MonoLabel color={alpha(ACCENT_2, 0.9)} size="0.57rem">
                    {selectedSet.size} selected
                  </MonoLabel>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Corner brackets — decorative */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.4, ml: 0.5 }}>
            {[0, 1].map(i => (
              <Box key={i} sx={{
                width: 8, height: 8,
                borderTop: `1.5px solid ${alpha(TEXT_PRIMARY, 0.12)}`,
                ...(i === 0 ? { borderLeft: `1.5px solid ${alpha(TEXT_PRIMARY, 0.12)}` } : { borderRight: `1.5px solid ${alpha(TEXT_PRIMARY, 0.12)}` }),
              }} />
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Table ── */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <TableContainer sx={{
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { height: 3, background: alpha(SURFACE_2, 0.5) },
          '&::-webkit-scrollbar-thumb': { background: alpha(ACCENT, 0.28), borderRadius: 2 },
        }}>
          <Table stickyHeader sx={{ tableLayout: 'auto', width: 'max-content', minWidth: { xs: 620, sm: '100%' } }}>
            <colgroup>
              {selectable && <col style={{ width: 44 }} />}
              {columns.map((col, ci) => <col key={ci} style={col.width ? { width: col.width } : undefined} />)}
            </colgroup>

            {/* ── Table head ── */}
            <TableHead>
              <TableRow>
                {selectable && (
                  <TableCell
                    padding="checkbox"
                    sx={{
                      bgcolor: alpha(SURFACE_3, 0.95),
                      borderBottom: `1px solid ${alpha(TEXT_PRIMARY, 0.07)}`,
                      width: 44,
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={allPageChecked}
                      indeterminate={someChecked && !allPageChecked}
                      onChange={toggleAll}
                      onClick={e => e.stopPropagation()}
                      sx={tableCheckboxSx}
                    />
                  </TableCell>
                )}

                {columns.map((col, ci) => {
                  const field      = col.sortKey ?? col.key;
                  const sortable   = col.sortable !== false && field != null;
                  const isActive   = sortField === field && field != null;

                  return (
                    <TableCell
                      key={ci}
                      align={col.align || 'left'}
                      onClick={sortable ? () => handleSort(col) : undefined}
                      onKeyDown={sortable ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort(col); } } : undefined}
                      tabIndex={sortable ? 0 : undefined}
                      aria-sort={sortable && isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : sortable ? 'none' : undefined}
                      sx={{
                        width: col.width, minWidth: col.minWidth ?? 100, maxWidth: col.maxWidth,
                        bgcolor: alpha(SURFACE_3, 0.95),
                        backgroundImage: isActive
                          ? `linear-gradient(135deg, ${alpha(ACCENT, 0.14)}, ${alpha(ACCENT_2, 0.06)})`
                          : 'none',
                        borderBottom: `1px solid ${isActive ? alpha(ACCENT, 0.35) : alpha(TEXT_PRIMARY, 0.07)}`,
                        py: 1.4,
                        px: { xs: 1.5, sm: 2, md: 3 },
                        whiteSpace: 'nowrap',
                        cursor: sortable ? 'pointer' : 'default',
                        userSelect: 'none',
                        transition: 'background-image 0.18s ease, border-color 0.18s ease',
                        verticalAlign: 'middle',
                        outline: 'none',
                        position: 'relative',

                        '&:hover': sortable ? {
                          backgroundImage: `linear-gradient(135deg, ${alpha(ACCENT, 0.1)}, ${alpha(ACCENT_2, 0.04)})`,
                          '& .h-label': { color: ACCENT },
                          '& .h-underline': { width: '100%', opacity: 1 },
                        } : {},
                        '&:focus-visible': { outline: `2px solid ${alpha(ACCENT, 0.45)}`, outlineOffset: '-2px' },
                      }}
                    >
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0.7,
                        justifyContent: col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start',
                        position: 'relative',
                      }}>
                        {col.icon && (
                          <col.icon sx={{
                            fontSize: 11,
                            color: isActive ? ACCENT : alpha(TEXT_SECONDARY, 0.4),
                            transition: 'color 0.18s',
                          }} />
                        )}
                        <MonoLabel
                          className="h-label"
                          color={isActive ? ACCENT : alpha(TEXT_SECONDARY, 0.55)}
                          size="0.62rem"
                          sx={{ transition: 'color 0.18s ease', letterSpacing: '0.1em' }}
                        >
                          {col.label}
                        </MonoLabel>
                        {sortable && <SortIcon active={isActive} direction={sortDir} />}

                        {/* Hover underline */}
                        {sortable && (
                          <Box className="h-underline" sx={{
                            position: 'absolute', bottom: -8, left: 0,
                            height: '2px', borderRadius: '2px',
                            width: isActive ? '100%' : 0,
                            opacity: isActive ? 1 : 0,
                            background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_2})`,
                            transition: 'width 0.2s ease, opacity 0.2s ease',
                          }} />
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            {/* ── Table body ── */}
            <TableBody>
              {loading ? (
                <LoadingState colSpan={colCount} />
              ) : paginatedData.length === 0 ? (
                <EmptyState
                  colSpan={colCount}
                  icon={emptyState.icon}
                  title={emptyState.title}
                  description={emptyState.description}
                  action={emptyState.action}
                />
              ) : (
                paginatedData.map((row, ri) => {
                  const rowKey    = getRowId(row) ?? `${page}-${ri}`;
                  const isChecked = selectable && selectedSet.has(String(rowKey));
                  const isHovered = hoveredRow === ri;

                  return (
                    <motion.tr
                      key={`${animKey}-${page}-${rowKey}`}
                      custom={ri}
                      variants={prefersReduced ? {} : rowVariants}
                      initial="hidden"
                      animate="visible"
                      onClick={() => onRowClick?.(row, ri)}
                      onMouseEnter={() => setHoveredRow(ri)}
                      onMouseLeave={() => setHoveredRow(null)}
                      onKeyDown={e => {
                        if ((e.key === 'Enter' || e.key === ' ') && onRowClick) {
                          e.preventDefault();
                          onRowClick(row, ri);
                        }
                      }}
                      tabIndex={onRowClick ? 0 : -1}
                      aria-selected={isChecked}
                      style={{
                        cursor: onRowClick ? 'pointer' : 'default',
                        position: 'relative',
                        outline: 'none',
                      }}
                    >
                      {selectable && (
                        <TableCell
                          padding="checkbox"
                          onClick={e => e.stopPropagation()}
                          sx={{
                            ...SHARED_CELL_SX,
                            background: isChecked ? alpha(ACCENT, 0.1) : isHovered ? alpha(ACCENT, 0.04) : 'transparent',
                            // left accent stripe when selected
                            position: 'relative',
                            '&::before': isChecked ? {
                              content: '""',
                              position: 'absolute',
                              left: 0, top: 0, bottom: 0,
                              width: 3,
                              background: `linear-gradient(180deg, ${ACCENT}, ${ACCENT_2})`,
                              borderRadius: '0 2px 2px 0',
                            } : {},
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={isChecked}
                            onChange={e => toggleRow(row, e)}
                            onClick={e => e.stopPropagation()}
                            sx={tableCheckboxSx}
                          />
                        </TableCell>
                      )}

                      {columns.map((col, ci) => {
                        const tone      = getColumnTone(col, ci);
                        const isPrimary = tone === 'primary';
                        const isSortCol = (col.sortKey ?? col.key) === sortField;
                        const align     = col.align || 'left';
                        const cellValue = renderCell(col, row, ri);
                        const isTextual = typeof cellValue === 'string' || typeof cellValue === 'number';
                        const needsTip  = isTextual && String(cellValue).length > 30;

                        return (
                          <TableCell
                            key={ci}
                            align={align}
                            sx={{
                              ...SHARED_CELL_SX,
                              width: col.width, minWidth: col.minWidth ?? 100, maxWidth: col.maxWidth,
                              // Subtle column sort tint
                              background: isChecked
                                ? alpha(ACCENT, 0.1)
                                : isHovered
                                  ? isSortCol ? alpha(ACCENT, 0.07) : alpha(ACCENT, 0.04)
                                  : isSortCol ? alpha(ACCENT, 0.03) : (ri % 2 !== 0 ? alpha(ACCENT_2, 0.02) : 'transparent'),
                              // Primary column: brighter text
                              ...(isPrimary && {
                                fontWeight: 600,
                                color: alpha(TEXT_PRIMARY, 0.95),
                                fontSize: '0.87rem',
                              }),
                              // Secondary column: dimmer
                              ...(tone === 'secondary' && {
                                color: alpha(TEXT_SECONDARY, 0.65),
                                fontSize: '0.8rem',
                              }),
                              ...(tone === 'action' && {
                                '& .MuiIconButton-root[data-action="view"]': actionBtnSx(ACTION_VIEW),
                                '& .MuiIconButton-root[data-action="edit"]': actionBtnSx(ACTION_EDIT),
                                '& .MuiIconButton-root[data-action="delete"]': actionBtnSx(ACTION_DELETE),
                              }),
                              '&:not(:last-child)': { borderRight: `1px solid ${alpha(TEXT_PRIMARY, 0.04)}` },
                            }}
                          >
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
                              minWidth: 0, width: '100%',
                            }}>
                              {isTextual ? (
                                <Tooltip
                                  title={needsTip ? cellValue : ''}
                                  placement="top"
                                  componentsProps={{ tooltip: {
                                    sx: {
                                      fontFamily: "'DM Sans', sans-serif",
                                      fontSize: '0.72rem',
                                      background: alpha(SURFACE_3, 0.97),
                                      border: `1px solid ${alpha(ACCENT, 0.2)}`,
                                      color: alpha(TEXT_PRIMARY, 0.9),
                                      borderRadius: RADIUS_MD,
                                      boxShadow: `0 8px 24px ${alpha('#000', 0.4)}`,
                                    },
                                  }}}
                                >
                                  <Box sx={{
                                    minWidth: 0, maxWidth: '100%',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                  }}>
                                    {cellValue}
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                                  {cellValue}
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                        );
                      })}
                    </motion.tr>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Pagination ── */}
        {onPageChange && onRowsPerPageChange && (
          <TableListPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={sortedData.length}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={rowsPerPageOptions}
          />
        )}
      </Box>
    </motion.div>
  );
};

export default TableList;
